import { useEffect, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";

// Official EasyDMARC domain-scanner embed. Token is a public embed token (client-side by design).
const SRC = "https://easydmarc.com/tools/domain-scanner/embedjs/1.0.0";
const DATA_ID = "9ltfnu";
const DATA_TOKEN =
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjlsdGZudSIsImhlaWdodCI6ImF1dG8iLCJ0eXBlIjoiZG9tYWluLXNjYW5uZXIiLCJ3aWR0aCI6IjEwMCUiLCJib3hfc2hhZG93IjoiMCAwIDEwcHggIzAwMDAwMDI2IiwiYm9yZGVyX3JhZGl1cyI6IjhweCIsImF1dG9pbml0IjoidHJ1ZSIsIm9wdGlvbnMiOnsic3R5bGVzIjp7InRoZW1lIjp7InRpdGxlQ29sb3IiOiIjMUEzMzgwIiwicGFyYWdyYXBoQ29sb3IiOiIjNUE1RTcyIiwiYnV0dG9uc0NvbG9yIjoiIzMzNjZGRiIsInNoYWRvd0NvbG9yIjoiIzMzNjZGRjIwIiwic2hhZG93Q2hlY2siOiJ0cnVlIiwidGhlbWVfbW9kZSI6ImxpZ2h0In19LCJjb250ZW50Ijp7InRpdGxlIjoiRG9tYWluIFNlY3VyaXR5IEhlYWx0aCBDaGVjayIsInBhcmFncmFwaCI6IkF0dGFja2VycyBpbXBlcnNvbmF0ZSByZWFsIGJ1c2luZXNzZXMgZXZlcnkgZGF5IGJ5IHNwb29maW5nIHVucHJvdGVjdGVkIGRvbWFpbnMg4oCUIHNlbmRpbmcgcGhpc2hpbmcgZW1haWxzIHRoYXQgbG9vayBsaWtlIHRoZXkgY2FtZSBmcm9tIHlvdS4gUnVuIGEgZnJlZSBzY2FuIGJlbG93IHRvIGNoZWNrIHlvdXIgZG9tYWluJ3MgRE1BUkMsIFNQRiwgYW5kIERLSU0gY29uZmlndXJhdGlvbiwgYW5kIHNlZSBleGFjdGx5IHdoZXJlIHlvdSdyZSBleHBvc2VkLiIsImJ1dHRvbl8xIjoiU2NhbiBNeSBEb21haW4iLCJidXR0b25fMiI6IkNsb3NlIFRoZXNlIEdhcHMiLCJyZWRpcmVjdF91cmwiOiJodHRwczovL2Vhc2lsaW5rZWQuY29tL2NvbnRhY3QiLCJkZWFjdGl2ZV93aWRnZXRfbGluayI6ImZhbHNlIn0sImVkaXRpb24iOiJtc3AiLCJiaW1pX2FjdGl2YXRpb24iOiJmYWxzZSIsIm9yZ2FuaXphdGlvbiI6eyJvYmplY3RJZCI6Im9yZ182YTUxMzJlMjcyN2EyMmUwOWJkMjE5YmYiLCJkb21haW4iOiJlYXNpbGlua2VkLmNvbSJ9fSwiZW1iZWRfdmVyc2lvbiI6IjEuMC4wIiwiZW1iZWRfcmVkaXJlY3RfdXJsIjoiaHR0cHM6Ly9lYXNpbGlua2VkLmNvbS9jb250YWN0IiwiaWF0IjoxNzg2MzM0NjUxfQ.Mklsb-gR8Ny62yja6XoIpuk6Nv9jJgbisD__Un4dkIY";

const IFRAME_TITLE = "EasyDMARC domain security scanner";

// Loads the live EasyDMARC widget. Only rendered after the visitor asks for it, because
// the embed loads EasyDMARC's own marketing and analytics trackers inside its frame.
const EasyDmarcEmbed = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;
        // Prevent duplicate injection during re-renders / StrictMode double-invoke.
        if (container.querySelector("script[data-easydmarc]")) return undefined;

        const script = document.createElement("script");
        script.src = SRC;
        script.async = true;
        script.setAttribute("data-id", DATA_ID);
        script.setAttribute("data-token", DATA_TOKEN);
        script.setAttribute("data-easydmarc", "true");
        container.appendChild(script);

        // The embed's iframe has no title; add one so screen readers can name it (WCAG 4.1.2).
        const label = () =>
            container.querySelectorAll("iframe:not([title])").forEach((f) => f.setAttribute("title", IFRAME_TITLE));
        const observer = new MutationObserver(label);
        observer.observe(container, { childList: true, subtree: true });
        label();

        // No script teardown: the EasyDMARC embed loads asynchronously and inserts its
        // widget relative to this <script> node. Detaching the node mid-load (e.g. React
        // StrictMode's transient unmount) breaks the embed. React removes the whole subtree
        // on real unmount; the querySelector guard above prevents duplicate embeds.
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={containerRef}
            data-testid="easydmarc-scanner"
            className="w-full"
            aria-live="polite"
            aria-label={IFRAME_TITLE}
        />
    );
};

// Click-to-load card. Visually matches the EasyDMARC widget's own card (white, same title,
// copy and button colours) so the hero looks the same; the third-party embed and its
// trackers load only when the visitor chooses to run a scan.
export const EasyDmarcScanner = () => {
    const [active, setActive] = useState(false);

    if (active) return <EasyDmarcEmbed />;

    return (
        <div
            data-testid="easydmarc-facade"
            className="w-full rounded-lg bg-white p-6 text-left sm:p-8"
            style={{ boxShadow: "0 0 10px #00000026" }}
        >
            <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#3366FF]/10">
                <ShieldCheck className="h-5 w-5 text-[#3366FF]" aria-hidden="true" />
            </div>
            <h2 className="text-xl font-bold text-[#1A3380] sm:text-2xl">Domain Security Health Check</h2>
            <p className="mt-3 text-sm leading-relaxed text-[#5A5E72]">
                Attackers impersonate real businesses every day by spoofing unprotected domains — sending
                phishing emails that look like they came from you. Run a free scan to check your domain's DMARC,
                SPF, and DKIM configuration, and see exactly where you're exposed.
            </p>
            <button
                type="button"
                onClick={() => setActive(true)}
                data-testid="easydmarc-start"
                className="mt-6 inline-flex w-full items-center justify-center rounded-md bg-[#3366FF] px-5 py-3 text-sm font-semibold text-white transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3366FF] focus-visible:ring-offset-2"
            >
                Scan My Domain
            </button>
            <p className="mt-3 text-xs leading-relaxed text-[#5A5E72]">
                Powered by our partner EasyDMARC. Their scanner loads when you click and uses its own cookies.
            </p>
        </div>
    );
};
